import { useState, useEffect, useMemo, useCallback, Fragment } from 'react';
import { ChevronDown, ChevronRight, Bed, Bath, Maximize2, ArrowRight, DollarSign } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import type { UnitConfiguration, PropertyUnit } from '@/lib/units';
import { formatFloorDisplay } from '@/lib/utils';
import { getConfigurationUnitsCount } from '@/lib/units';
import { formatCurrency } from '@/lib/utils';
import { cn } from '@/lib/utils';

interface HierarchicalConfigurationViewProps {
  configurations: UnitConfiguration[];
  units: PropertyUnit[];
  onConfigurationClick: (configuration: UnitConfiguration) => void;
  onUnitClick: (unit: PropertyUnit) => void;
  className?: string;
}

interface HierarchicalGroup {
  name: string;
  configurations: UnitConfiguration[];
  totalCount: number;
  availableCount: number;
  reservedCount: number;
  soldCount: number;
  priceRange: { min: number | null; max: number | null };
}

type SortField = 'name' | 'total' | 'available' | 'price';
type SortOrder = 'asc' | 'desc';

export const HierarchicalConfigurationView = ({
  configurations,
  units,
  onConfigurationClick,
  onUnitClick,
  className,
}: HierarchicalConfigurationViewProps) => {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [counts, setCounts] = useState<Record<string, { available: number; reserved: number; sold: number }>>({});
  const [loadingCounts, setLoadingCounts] = useState(true);

  // Get units for a configuration
  const getConfigUnits = useCallback((config: UnitConfiguration): PropertyUnit[] => {
    return units.filter((unit) => {
      return (
        unit.block === config.block &&
        unit.unit_type === config.unit_type &&
        unit.bedrooms === config.bedrooms &&
        unit.bathrooms === config.bathrooms &&
        (config.location === 'Canal View' ? unit.view_type === 'canal' : unit.view_type === 'lagoon')
      );
    });
  }, [units]);

  // Calculate counts from units prop (reactive to filtering)
  useEffect(() => {
    if (configurations.length === 0 || units.length === 0) {
      setLoadingCounts(false);
      return;
    }

    setLoadingCounts(true);
    const countsMap: Record<string, { available: number; reserved: number; sold: number }> = {};

    configurations.forEach((config) => {
      const configUnits = getConfigUnits(config);
      countsMap[config.id] = {
        available: configUnits.filter(u => u.availability === 'available').length,
        reserved: configUnits.filter(u => u.availability === 'reserved').length,
        sold: configUnits.filter(u => u.availability === 'sold').length,
      };
    });

    setCounts(countsMap);
    setLoadingCounts(false);
  }, [configurations, units, getConfigUnits]);

  // Group configurations by name
  const groupedConfigurations = useMemo(() => {
    const groups = new Map<string, HierarchicalGroup>();

    configurations.forEach((config) => {
      const configCounts = counts[config.id] || { available: 0, reserved: 0, sold: 0 };
      
      if (!groups.has(config.name)) {
        groups.set(config.name, {
          name: config.name,
          configurations: [],
          totalCount: 0,
          availableCount: 0,
          reservedCount: 0,
          soldCount: 0,
          priceRange: { min: null, max: null },
        });
      }

      const group = groups.get(config.name)!;
      group.configurations.push(config);
      group.totalCount += config.total_units;
      group.availableCount += configCounts.available;
      group.reservedCount += configCounts.reserved;
      group.soldCount += configCounts.sold;

      // Update price range
      if (config.price !== null && config.price !== undefined) {
        if (group.priceRange.min === null || config.price < group.priceRange.min) {
          group.priceRange.min = config.price;
        }
        if (group.priceRange.max === null || config.price > group.priceRange.max) {
          group.priceRange.max = config.price;
        }
      }
    });

    return Array.from(groups.values());
  }, [configurations, counts]);

  // Sort groups
  const sortedGroups = useMemo(() => {
    return [...groupedConfigurations].sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      switch (sortField) {
        case 'name':
          aValue = a.name;
          bValue = b.name;
          break;
        case 'total':
          aValue = a.totalCount;
          bValue = b.totalCount;
          break;
        case 'available':
          aValue = a.availableCount;
          bValue = b.availableCount;
          break;
        case 'price':
          aValue = a.priceRange.min || 0;
          bValue = b.priceRange.min || 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [groupedConfigurations, sortField, sortOrder]);

  const toggleGroup = (groupName: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupName)) {
      newExpanded.delete(groupName);
    } else {
      newExpanded.add(groupName);
    }
    setExpandedGroups(newExpanded);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const formatArea = (area: number | null) => {
    if (!area) return 'N/A';
    return `${area.toLocaleString()} sq.ft`;
  };

  const formatPriceRange = (priceRange: { min: number | null; max: number | null }) => {
    if (priceRange.min === null && priceRange.max === null) {
      return null;
    }
    if (priceRange.min === priceRange.max) {
      return formatCurrency(priceRange.min!);
    }
    return `From ${formatCurrency(priceRange.min!)} to ${formatCurrency(priceRange.max!)}`;
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return null;
    return <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>;
  };

  return (
    <div className={cn('w-full', className)}>
      <div className="rounded-lg border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12"></TableHead>
              <TableHead 
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => handleSort('name')}
              >
                Configuration Name
                <SortIcon field="name" />
              </TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Beds/Baths</TableHead>
              <TableHead>
                Area
              </TableHead>
              <TableHead 
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => handleSort('price')}
              >
                Price
                <SortIcon field="price" />
              </TableHead>
              <TableHead 
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => handleSort('available')}
              >
                Available
                <SortIcon field="available" />
              </TableHead>
              <TableHead>Reserved</TableHead>
              <TableHead>Sold</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedGroups.map((group) => {
              const isExpanded = expandedGroups.has(group.name);
              const hasMultipleVariants = group.configurations.length > 1;

              return (
                <Fragment key={`group-${group.name}`}>
                  {/* Parent Row - Group Header */}
                  <TableRow
                    className={cn(
                      "cursor-pointer hover:bg-muted/50",
                      hasMultipleVariants && "bg-muted/30 font-semibold"
                    )}
                    onClick={() => hasMultipleVariants && toggleGroup(group.name)}
                  >
                    <TableCell>
                      {hasMultipleVariants && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleGroup(group.name);
                          }}
                        >
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )}
                        </Button>
                      )}
                    </TableCell>
                    <TableCell className="font-semibold">
                      {group.name}
                      {hasMultipleVariants && (
                        <span className="text-xs text-muted-foreground ml-2">
                          ({group.configurations.length} variants)
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {hasMultipleVariants ? (
                        <span className="text-muted-foreground">Multiple</span>
                      ) : (
                        <Badge variant="outline">{group.configurations[0].location}</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {hasMultipleVariants ? (
                        <span className="text-muted-foreground">Varies</span>
                      ) : (
                        <div className="flex items-center gap-2 text-sm">
                          <div className="flex items-center gap-1">
                            <Bed className="w-3 h-3" />
                            <span>{group.configurations[0].bedrooms}</span>
                          </div>
                          <span>/</span>
                          <div className="flex items-center gap-1">
                            <Bath className="w-3 h-3" />
                            <span>{group.configurations[0].bathrooms}</span>
                          </div>
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {hasMultipleVariants ? (
                        <span className="text-muted-foreground">Varies</span>
                      ) : (
                        <div className="flex items-center gap-1 text-sm">
                          <Maximize2 className="w-3 h-3 text-muted-foreground" />
                          <span>{formatArea(group.configurations[0].total_area_sq_ft)}</span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {loadingCounts ? (
                        <Skeleton className="h-5 w-24" />
                      ) : (
                        <div className="flex items-center gap-1 text-brand-teal font-semibold">
                          <DollarSign className="w-4 h-4" />
                          <span className="text-sm">
                            {formatPriceRange(group.priceRange) || 'Price on request'}
                          </span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {loadingCounts ? (
                        <Skeleton className="h-5 w-8" />
                      ) : (
                        <Badge className="bg-status-available text-white border-status-available">
                          {group.availableCount}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {loadingCounts ? (
                        <Skeleton className="h-5 w-8" />
                      ) : (
                        group.reservedCount > 0 ? (
                          <Badge className="bg-status-reserved text-white border-status-reserved">
                            {group.reservedCount}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">0</span>
                        )
                      )}
                    </TableCell>
                    <TableCell>
                      {loadingCounts ? (
                        <Skeleton className="h-5 w-8" />
                      ) : (
                        group.soldCount > 0 ? (
                          <Badge variant="destructive">
                            {group.soldCount}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">0</span>
                        )
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {!hasMultipleVariants && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            onConfigurationClick(group.configurations[0]);
                          }}
                        >
                          View Units
                          <ArrowRight className="w-4 h-4 ml-1" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>

                  {/* Child Rows - Individual Configurations (when expanded) */}
                  {isExpanded && group.configurations.map((config) => {
                    const configCounts = counts[config.id] || { available: 0, reserved: 0, sold: 0 };

                    return (
                      <TableRow
                        key={config.id}
                        className="cursor-pointer hover:bg-muted/50 bg-muted/10"
                        onClick={() => onConfigurationClick(config)}
                      >
                        <TableCell></TableCell>
                        <TableCell className="pl-8">
                          <div className="flex items-center gap-2">
                            <span className="text-sm">{config.unit_type}</span>
                            <Badge variant="outline" className="text-xs">
                              {formatFloorDisplay(config.floor)}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{config.location}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-sm">
                            <div className="flex items-center gap-1">
                              <Bed className="w-3 h-3" />
                              <span>{config.bedrooms}</span>
                            </div>
                            <span>/</span>
                            <div className="flex items-center gap-1">
                              <Bath className="w-3 h-3" />
                              <span>{config.bathrooms}</span>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm">
                            <Maximize2 className="w-3 h-3 text-muted-foreground" />
                            <span>{formatArea(config.total_area_sq_ft)}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {config.price !== null && config.price !== undefined ? (
                            <div className="flex items-center gap-1 text-brand-teal font-semibold">
                              <DollarSign className="w-4 h-4" />
                              <span className="text-sm">{formatCurrency(config.price)}</span>
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm">Price on request</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-status-available text-white border-status-available">
                            {configCounts.available}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {configCounts.reserved > 0 ? (
                            <Badge className="bg-status-reserved text-white border-status-reserved">
                              {configCounts.reserved}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">0</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {configCounts.sold > 0 ? (
                            <Badge variant="destructive">
                              {configCounts.sold}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">0</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              onConfigurationClick(config);
                            }}
                          >
                            View Units
                            <ArrowRight className="w-4 h-4 ml-1" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {sortedGroups.length === 0 && (
        <div className="text-center py-12">
          <p className="text-muted-foreground">No configurations found</p>
        </div>
      )}
    </div>
  );
};

